import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { apiClient } from 'Helpers/network';

import ProjectPeriodContext from 'Context/project_plan/ProjectPeriodContext';

import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

const ProjectPlanContext = createContext();

const PROJECT_GANTT_CHART_EXPANDED_ROWS_KEY = 'project_gantt_chart_expanded_rows';
// Every consumer can read `filters.owners` and friends without guarding for undefined. One entry
// per map the filter panel drives, plus the free-text search.
export const FILTER_MAP_KEYS = ['owners', 'statuses', 'source_categories', 'source_departments', 'source_venues', 'schedule'];
const DEFAULT_FILTERS = { search: undefined, owners: {}, statuses: {}, source_categories: {}, source_departments: {}, source_venues: {}, schedule: {} };

/**
 * The server's saved selections over the defaults. PHP encodes an empty array as `[]` and a map
 * whose only key is `0` as `[true]`, so both levels are normalised to plain objects. Search is
 * never restored.
 */
const initialFilters = (savedFilters) => {
    const saved = savedFilters && typeof savedFilters === 'object' && !Array.isArray(savedFilters) ? savedFilters : {};
    const maps = {};

    FILTER_MAP_KEYS.forEach((key) => {
        if (saved[key] && typeof saved[key] === 'object') {
            maps[key] = Object.fromEntries(Object.entries(saved[key]));
        }
    });

    return { ...DEFAULT_FILTERS, ...maps, search: undefined };
};

// Only the ticked entries of each map: what the server stores and what counts as an applied filter.
export const selectedFilterMaps = (filters) =>
    FILTER_MAP_KEYS.reduce((maps, key) => {
        const selected = Object.entries(filters?.[key] || {}).filter(([, value]) => value === true || value === 'true');

        if (selected.length) {
            maps[key] = Object.fromEntries(selected.map(([value]) => [value, true]));
        }

        return maps;
    }, {});

/**
 * Seed the column overlay from an initial (windowed) gantt tree: recursively
 * collect every row's non-empty `columns` map keyed by its `data.key`.
 */
const seedColumnsFromRows = (rows, target = {}) => {
    (rows || []).forEach((row) => {
        if (row.columns && Object.keys(row.columns).length) {
            target[row.data.key] = row.columns;
        }

        if (row.items?.length) {
            seedColumnsFromRows(row.items, target);
        }
    });

    return target;
};

const getPersistedExpandedRowsCollapsed = () => {
    return localStorage.getItem(PROJECT_GANTT_CHART_EXPANDED_ROWS_KEY) ? JSON.parse(localStorage.getItem(PROJECT_GANTT_CHART_EXPANDED_ROWS_KEY)) : {};
};

export const ProjectPlanProvider = ({
    fetchProjectGanttChartUrl,
    fetchProjectGanttChartLinksUrl,
    moveProjectGanttChartUrl,
    deleteProjectPlanUrl,
    updateProjectCategoryOwnerUrl,
    updateProjectActivityOwnerUrl,
    fetchProjectResourcesUrl,
    fetchProjectActivityResourcesUrl,
    allocateProjectResourcesUrl,
    fetchProjectActivityDependenciesUrl,
    searchProjectActivityDependencyCandidatesUrl,
    searchProjectCategoryDependencyCandidatesUrl,
    dependencyTypes = {},
    activityTypes,
    sourceCategories = [],
    statuses,
    completenessStatuses,
    canManage,
    projectSourceType,
    projectSourceId,
    projectSourceCategory = 'master',
    savedFilters,
    saveFiltersUrl,
    fetchScheduleUrl,
    saveScheduleTargetUrl,
    savedScheduleTargetId = null,
    children,
}) => {
    const [expandedRows, setExpandedRows] = useState(getPersistedExpandedRowsCollapsed());
    const [deletableRows, setDeletableRows] = useState(new Set());
    const [disabledDeletionRows, setDisabledDeletionRows] = useState(new Set());
    const [filters, setFilters] = useState(() => initialFilters(savedFilters));
    const [deletable, setDeletable] = useState(false);
    const [projectGanttChartProgress, setProjectGanttChartProgress] = useState(0);

    // The key milestone the user picked for this plan. The server falls back to the plan's default
    // whenever this is not one of the plan's key milestones, and the schedule reports the target it
    // actually used, so this is never corrected client-side.
    const [targetId, setTargetId] = useState(savedScheduleTargetId);

    // Kept for the session only: a sorted register is a way of looking, not a setting.
    const [sortByFloat, setSortByFloat] = useState(false);

    // Column-windowing overlay (IB-814): bar/completion geometry keyed by
    // activity key then column key, the contiguous date range currently
    // loaded, and the today-marker line. The row tree still comes from the
    // gantt request; only the column axis is windowed/lazy-loaded.
    const [columnsByKey, setColumnsByKey] = useState({});
    const [loadedRange, setLoadedRange] = useState(null);
    const [line, setLine] = useState(null);

    // The date the server built its window around, which is where the timeline scrolls to — on load
    // and again whenever a refetch moves it. Deliberately NOT the first activity date: that is null
    // when a filter matches nothing, and the scroll still has to land somewhere. The server has
    // already made that decision (first activity, else today, else event start, clamped into the
    // event) and the scroll must agree with the window it fetched, so it is not re-derived here —
    // a client-side "today" can fall on a different date to the server's across timezones.
    const [anchorDate, setAnchorDate] = useState(null);

    const isMaster = useMemo(() => projectSourceCategory === 'master', [projectSourceCategory]);

    const { period } = useContext(ProjectPeriodContext);

    const toast = useToast();

    // Mirror loadedRange into a ref so loadColumnWindow can dedupe against the
    // latest range without being re-created (and re-triggering scroll effects)
    // on every extension.
    const loadedRangeRef = useRef(null);
    useEffect(() => {
        loadedRangeRef.current = loadedRange;
    }, [loadedRange]);

    // The band the user last scrolled to (visible columns plus the scroll buffer), which every
    // scroll load reports. A refetch that keeps the window asks for this band rather than the
    // whole of `loadedRange`, which grows with every contiguous scroll.
    const visibleBandRef = useRef(null);

    // In-flight window keys, to avoid firing duplicate requests for the same
    // chunk while one is already pending.
    const inFlightWindowsRef = useRef(new Set());

    // Monotonic fetch generation. Bumped whenever a NEW column intent starts
    // (initial load, a scroll load, or a period/filter reset). Each dispatched
    // fetch captures the generation at dispatch time and, when it resolves,
    // bails before touching state if the generation has since moved on — so a
    // late/out-of-order response can never overwrite the overlay for a band the
    // user has scrolled away from, or write stale-period columns after a reset.
    const fetchGenerationRef = useRef(0);

    // Whether the tree request in flight keeps the loaded window, so the rows already on screen
    // can stay there while it loads. Only meaningful while `loading` is true.
    const [keepingWindow, setKeepingWindow] = useState(false);

    // Bumped by every refetch that follows an edit, which may have changed dependency links or bar
    // dates. Each gantt response carries the revision it was requested at, so the dependency
    // arrows are only drawn from links fetched for the same revision. Period, filter and search
    // changes leave it alone: they reshape the rows but not the links.
    const linksRevisionRef = useRef(0);
    const [linksRevision, setLinksRevision] = useState(0);

    // An anchored request deliberately sends NO from/to. That is what tells the server to anchor
    // the window on the plan's first activity instead of on today, and it owns the per-period
    // buffer sizes that used to be duplicated here. `loadedRange` is then whatever window
    // the server says it generated. Scroll-triggered loads still send an explicit from/to.
    //
    // Only a period, filter or target change anchors. Any other refetch follows a change to the
    // plan, and asks for the band the user is looking at so the timeline stays where they left it.
    const projectGanttChartRequest = useQuery({
        enabled: false,
        queryFn: ({ keepWindow = false } = {}) => {
            setProjectGanttChartProgress(0);

            const keptRange = keepWindow ? (visibleBandRef.current ?? loadedRangeRef.current) : null;

            const generation = ++fetchGenerationRef.current;

            // What this response was narrowed by, kept with it so the empty state describes the
            // data on screen rather than filters whose refetch has not started yet.
            const requestedLinksRevision = linksRevisionRef.current;

            const requested = {
                filtered: Object.keys(selectedFilterMaps(filters)).length > 0,
                search: filters.search?.trim() || '',
            };

            const promise = axios.get(fetchProjectGanttChartUrl.replace('PERIOD_TYPE', period), {
                params: {
                    ...filters,
                    source_type: projectSourceType,
                    source_id: projectSourceId,
                    target_id: targetId ?? undefined,
                    from: keptRange?.from,
                    to: keptRange?.to,
                },
                onDownloadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setProjectGanttChartProgress(percentCompleted);
                },
            });

            // Seed the overlay from the initial (windowed) tree's activity
            // columns, guarded by the captured generation so a late response
            // from a superseded period/filter never lands on the new axis.
            promise
                .then((response) => {
                    if (generation !== fetchGenerationRef.current) {
                        return;
                    }
                    setColumnsByKey(seedColumnsFromRows(response.data?.rows));
                    setLine(response.data?.line || null);
                    setLoadedRange(response.data?.window || null);
                    if (!keptRange) {
                        setAnchorDate(response.data?.anchor_date || null);
                    }
                })
                .catch(() => {
                    // Errors are surfaced by useQuery's own handling.
                });

            // A response from a superseded request never reaches state: it would overwrite the
            // newer rows and bring back the empty state of filters that have since been cleared.
            // It stays pending, so the newer request owns `loading`.
            return promise.then((response) =>
                generation !== fetchGenerationRef.current
                    ? new Promise(() => {})
                    : { ...response, data: { ...response.data, requested, linksRevision: requestedLinksRevision } },
            );
        },
    });

    // Owner and status filters only narrow the activities, so a filter matching none still returns
    // every category row. The plan treats that response as having no rows.
    const filteredEmpty = !!projectGanttChartRequest.data?.requested?.filtered && projectGanttChartRequest.data.activity_count === 0;

    // The target only narrows the tree through a Schedule filter, so without one a target change
    // leaves the tree alone.
    const hasScheduleFilter = Object.values(filters.schedule || {}).some((value) => value === true || value === 'true');
    const treeTargetId = hasScheduleFilter ? targetId : null;

    /**
     * Every tree refetch goes through here. The flag is set before the request starts: React 16
     * does not batch these updates, so `loading` commits on its own first, and the rows must
     * already know to stay.
     */
    const refetchTree = ({ anchor = false } = {}) => {
        const keepWindow = !anchor && !!loadedRangeRef.current;
        setKeepingWindow(keepWindow);

        return projectGanttChartRequest.refetch({ keepWindow });
    };

    const scheduleGenerationRef = useRef(0);

    // The plan measured against its target: floats, state lists, key milestones and the pill
    // counts. It ignores the filters, so it is refetched only when the plan or the target changes.
    const scheduleRequest = useQuery({
        enabled: false,
        displayErrorToast: false,
        queryFn: () => {
            const generation = ++scheduleGenerationRef.current;

            return (
                axios
                    .get(fetchScheduleUrl, {
                        params: {
                            source_type: projectSourceType,
                            source_id: projectSourceId,
                            target_id: targetId ?? undefined,
                        },
                    })
                    // A superseded response never reaches state, success or failure alike: a stale
                    // failure would otherwise mark the newer schedule as failed.
                    .then(
                        (response) => (generation !== scheduleGenerationRef.current ? new Promise(() => {}) : response),
                        (error) => (generation !== scheduleGenerationRef.current ? new Promise(() => {}) : Promise.reject(error)),
                    )
            );
        },
    });

    const schedule = scheduleRequest.data?.data ?? null;

    // Read at call time, so a mutation's refetch always uses the current target.
    const scheduleRefetchRef = useRef(scheduleRequest.refetch);
    scheduleRefetchRef.current = scheduleRequest.refetch;

    const refetchSchedule = useCallback(() => {
        if (fetchScheduleUrl) {
            scheduleRefetchRef.current().catch(() => {});
        }
    }, [fetchScheduleUrl]);

    const resourcesRequest = useQuery({
        queryFn: () => apiClient.get(fetchProjectResourcesUrl),
    });

    // The column overlay is per-period and per-filter: drop it whenever either
    // changes so a stale window never bleeds across a reload. Bumping the
    // generation here invalidates any column fetch still in flight from before
    // the change. Declared BEFORE the refetch effect so it runs first — the
    // fresh initial fetch then captures the post-reset generation.
    useEffect(() => {
        fetchGenerationRef.current += 1;
        setColumnsByKey({});
        setLoadedRange(null);
        setLine(null);
        setAnchorDate(null);
        visibleBandRef.current = null;
        inFlightWindowsRef.current.clear();
    }, [period, filters, treeTargetId]);

    // A period, filter or target change refetches only the tree: the schedule ignores the filters,
    // and a target change refetches it on its own below.
    useEffect(() => {
        refetchTree({ anchor: true });
    }, [period, filters, treeTargetId]);

    useEffect(() => {
        refetchSchedule();
    }, []);

    /**
     * Refetch after a change to the plan itself (an edit, move or delete). The schedule is
     * refetched at once rather than when the tree lands, so a tree request a filter change then
     * supersedes still leaves the schedule up to date.
     */
    const refetchPlan = () => {
        refetchSchedule();

        return refetchTree();
    };

    const targetMountedRef = useRef(false);

    useEffect(() => {
        if (!targetMountedRef.current) {
            targetMountedRef.current = true;

            return;
        }

        refetchSchedule();
    }, [targetId]);

    const changeTarget = useCallback(
        (id) => {
            setTargetId(id);

            if (saveScheduleTargetUrl) {
                apiClient.put(saveScheduleTargetUrl, { target_id: id }).catch((error) => {
                    console.warn('Failed to save the critical path target', error);
                });
            }
        },
        [saveScheduleTargetUrl],
    );

    const toggleSortByFloat = useCallback(() => setSortByFloat((sorted) => !sorted), []);

    // Fetch a single lightweight (columns_only) chunk and stitch it into the
    // overlay. Guarded against duplicate in-flight requests for the same chunk.
    //
    // `reanchor` distinguishes the two ways loadColumnWindow calls this:
    //   - false (edge extend): MERGE into the overlay and grow loadedRange to
    //     stay a single contiguous band. It CAPTURES the generation without
    //     bumping (so it never strands a live fetch) and bails on resolve if a
    //     reset/re-anchor has since bumped it — closing the whole stale-merge
    //     class (disjoint AND overlapping cross-period) before touching state.
    //     A contiguity check on the loadedRange widen is kept as belt-and-braces.
    //   - true (jump landing):  REPLACE the overlay + loadedRange with just this
    //     band, dropping the un-scrolled-through gap. This keeps a fetch (and
    //     the overlay) bounded by the band width instead of the plan duration on
    //     a far scrollbar jump (AC#2). Because it REPLACES, it is generation-
    //     guarded so a superseded re-anchor can't clobber the current band.
    //
    // The generation is bumped HERE, only on the reanchor path and only AFTER
    // the in-flight dedupe guard — so a call that dispatches no new request
    // (deduped repeat) never bumps the generation and thus never strands the
    // live re-anchor it deduped against. A reanchor is always a single fetch,
    // so there is no left/right sharing concern.
    const fetchColumnChunk = useCallback(
        (fromDate, toDate, { reanchor = false } = {}) => {
            const rangeKey = `${fromDate}_${toDate}`;
            if (inFlightWindowsRef.current.has(rangeKey)) {
                return;
            }
            inFlightWindowsRef.current.add(rangeKey);

            // Re-anchor BUMPS (it supersedes prior intents); merge only CAPTURES
            // the current generation without bumping, so it never strands a live
            // fetch but still bails if a reset/re-anchor happened after dispatch.
            const generation = reanchor ? ++fetchGenerationRef.current : fetchGenerationRef.current;

            axios
                .get(fetchProjectGanttChartUrl.replace('PERIOD_TYPE', period), {
                    params: {
                        ...filters,
                        source_type: projectSourceType,
                        source_id: projectSourceId,
                        target_id: targetId ?? undefined,
                        from: fromDate,
                        to: toDate,
                        columns_only: 1,
                    },
                })
                .then((response) => {
                    const columns = response.data?.columns || {};

                    if (reanchor) {
                        // Drop a stale re-anchor: a newer intent (scroll/reset/
                        // reload) has superseded this REPLACE, so applying it
                        // would blank the band the user has moved to.
                        if (generation !== fetchGenerationRef.current) {
                            return;
                        }

                        setColumnsByKey(columns);
                        setLoadedRange({ from: fromDate, to: toDate });
                    } else {
                        // Drop a stale merge: a reset (period/filter change) or a
                        // re-anchor bumped the generation after this fetch was
                        // dispatched, so its columns belong to a superseded axis.
                        // Applying it could add unreadable cross-period columns and
                        // (worse) falsely widen loadedRange over never-loaded ones.
                        if (generation !== fetchGenerationRef.current) {
                            return;
                        }

                        setColumnsByKey((prev) => {
                            const next = { ...prev };
                            Object.entries(columns).forEach(([activityKey, columnMap]) => {
                                next[activityKey] = { ...(next[activityKey] || {}), ...columnMap };
                            });
                            return next;
                        });

                        // Widen loadedRange only when this band is still
                        // contiguous with the CURRENT range. If a reset happened
                        // (prev === null) or the band is now disjoint (e.g. a
                        // stale merge landing after a jump/reset), leave the range
                        // untouched — widening it would falsely mark unloaded
                        // columns as covered and permanently blank that band.
                        setLoadedRange((prev) => {
                            if (!prev || fromDate > prev.to || toDate < prev.from) {
                                return prev;
                            }

                            return { from: fromDate < prev.from ? fromDate : prev.from, to: toDate > prev.to ? toDate : prev.to };
                        });
                    }

                    if (response.data?.line) {
                        setLine(response.data.line);
                    }
                })
                .catch(() => {
                    // A failed window simply leaves those columns empty until the
                    // next scroll retries; no toast (this is a background fetch).
                })
                .finally(() => {
                    inFlightWindowsRef.current.delete(rangeKey);
                });
        },
        [fetchProjectGanttChartUrl, period, filters, projectSourceType, projectSourceId, targetId],
    );

    // Load columns for the visible landing band [fromDate, toDate] (already
    // capped to MAX_FETCH_COLUMNS columns by the scroll handler).
    //
    // The caller passes a BOUNDED band, so we must never widen it back out to
    // the full gap between the band and the current edge — that is exactly the
    // plan-duration-proportional fetch AC#2 forbids. Two cases keep loadedRange
    // a single contiguous band while every request stays band-sized:
    //   - Contiguous (band overlaps/touches the current range): extend only the
    //     uncovered edge(s); each delta is <= the band width because they share
    //     a boundary.
    //   - Jump (band is disjoint from the current range): re-anchor to the band
    //     and fetch only it, skipping the un-scrolled gap (refetched later if
    //     the user scrolls back through it).
    const loadColumnWindow = useCallback(
        (fromDate, toDate) => {
            if (!fromDate || !toDate) {
                return;
            }

            visibleBandRef.current = { from: fromDate, to: toDate };

            const current = loadedRangeRef.current;

            // Wait for the initial (or post-reset) seed to land before lazy-
            // loading: while it's in flight loadedRange is null, and the auto
            // scroll to the anchor can otherwise fire a redundant re-anchor
            // that races (and discards, via the generation guard) the seed.
            if (!current) {
                return;
            }

            // Fully covered: nothing to do.
            if (fromDate >= current.from && toDate <= current.to) {
                return;
            }

            // Jump (disjoint band): re-anchor to this band. fetchColumnChunk
            // bumps + captures the generation itself, after its dedupe guard, so
            // a deduped repeat can't strand the live re-anchor.
            if (fromDate > current.to || toDate < current.from) {
                fetchColumnChunk(fromDate, toDate, { reanchor: true });
                return;
            }

            // Contiguous: extend only the uncovered edge(s) — plain merge.
            if (fromDate < current.from) {
                fetchColumnChunk(fromDate, current.from);
            }

            if (toDate > current.to) {
                fetchColumnChunk(current.to, toDate);
            }
        },
        [fetchColumnChunk],
    );

    useEffect(() => {
        localStorage.setItem(PROJECT_GANTT_CHART_EXPANDED_ROWS_KEY, JSON.stringify(expandedRows));
    }, [expandedRows]);

    useEffect(() => {
        if (!deletable) {
            setDeletableRows(new Set());
            setDisabledDeletionRows(new Set());
        }
    }, [deletable]);

    // Keyed on the ticked selections alone, so typing in the search box never saves anything.
    const selectedFiltersKey = useMemo(() => JSON.stringify(selectedFilterMaps(filters)), [filters]);
    const savedFiltersKeyRef = useRef(selectedFiltersKey);
    // Saves run one after another so the last selection is the one stored.
    const saveQueueRef = useRef(Promise.resolve());

    useEffect(() => {
        if (!saveFiltersUrl || selectedFiltersKey === savedFiltersKeyRef.current) {
            return;
        }

        savedFiltersKeyRef.current = selectedFiltersKey;

        saveQueueRef.current = saveQueueRef.current
            .then(() => apiClient.put(saveFiltersUrl, { filters: JSON.parse(selectedFiltersKey) }))
            .catch((error) => {
                console.warn('Failed to save the project plan filters', error);
            });
    }, [saveFiltersUrl, selectedFiltersKey]);

    // Filters used to be kept in localStorage; drop what is left there.
    useEffect(() => {
        try {
            Object.keys(localStorage)
                .filter((key) => key.startsWith('project_plan_filters_'))
                .forEach((key) => localStorage.removeItem(key));
        } catch (e) {
            // Storage unavailable: nothing to clean up.
        }
    }, []);

    const changeFilters = useCallback(
        (newFilters) => {
            setFilters((filters) => ({ ...filters, ...newFilters }));
        },
        [setFilters],
    );

    const toggleRow = useCallback(
        (key) => {
            setExpandedRows((prev) => ({
                ...prev,
                [key]: !prev[key],
            }));
        },
        [setExpandedRows],
    );

    /**
     * Set a row's expansion explicitly, rather than flipping whatever is stored.
     *
     * `toggleRow` reads `!prev[key]`, which treats "never touched" as collapsed. That holds
     * for tree rows, which start collapsed, but not for the by-source bands (IB-793), which
     * start open — there the first click would compute `!undefined === true` and leave the
     * band exactly as it was.
     */
    const setRowExpanded = useCallback((key, expanded) => {
        setExpandedRows((prev) => ({
            ...prev,
            [key]: expanded,
        }));
    }, []);

    const toggleDeletable = useCallback(() => {
        setDeletable((state) => !state);
    }, [setDeletable]);

    const toggleDeletableRow = useCallback(
        (key) => {
            let values = new Set(deletableRows);
            let disabled = new Set(disabledDeletionRows);
            projectGanttChartRequest.data?.rows_keys?.forEach((rowKey) => {
                // If the current key is a child of the selected key or the selected key itself
                if (rowKey.includes(key)) {
                    // Disabling uncheck for child keys
                    // If the current key is not the selected key (the parent key).
                    if (rowKey !== key) {
                        // If the checkbox is ticked we disable the children checkboxes, else enable them.
                        !deletableRows.has(key) ? disabled.add(rowKey) : disabled.delete(rowKey);

                        // We should uncheck the children keys
                        values.delete(rowKey);
                    } else {
                        deletableRows.has(key) ? values.delete(rowKey) : values.add(rowKey);
                        // values[rowKey] = !deletableRows[key];
                    }
                }
            });
            setDeletableRows(values);
            setDisabledDeletionRows(disabled);
        },
        [projectGanttChartRequest.data, deletableRows, disabledDeletionRows, setDeletableRows, setDisabledDeletionRows],
    );

    const toggleAllRows = useCallback(
        (value = true) => {
            let state = {};
            projectGanttChartRequest.data?.rows_keys?.map((key) => {
                state[key] = value;
            });
            setExpandedRows(state);
        },
        [projectGanttChartRequest.data, setExpandedRows],
    );

    // Refetch after an edit that may have changed links or dates: moves the links revision first,
    // so the gantt request and the links request start together for the same revision.
    const refetchAfterEdit = (...args) => {
        linksRevisionRef.current += 1;
        setLinksRevision(linksRevisionRef.current);

        return refetchPlan(...args);
    };

    // `projectIds` narrows a Delete All to the ticked plans; left out, it clears every plan on the page.
    const removeProjectPlanItems = (projectIds) => {
        let keys = Array.from(deletableRows);

        if (deletable && !deletableRows.size) {
            return;
        }

        const data = { keys, all: !deletable, ...(projectIds ? { project_ids: projectIds } : {}) };

        apiClient.delete(deleteProjectPlanUrl, { data }).then(() => {
            toast.success('Items removed successfully');
            refetchAfterEdit();
        });
    };

    const updateProjectCategoryOwner = useCallback(
        (categoryId, ownerId) => {
            const url = updateProjectCategoryOwnerUrl.replace('CATEGORY_ID', categoryId);
            return apiClient
                .put(url, {
                    owner_id: ownerId,
                })
                .then(() => {
                    // An owner change cannot move float, so only the tree is refetched.
                    refetchTree();
                });
        },
        [updateProjectCategoryOwnerUrl, toast, refetchTree],
    );

    const updateProjectActivityOwner = useCallback(
        (activityId, ownerId) => {
            const url = updateProjectActivityOwnerUrl.replace('ACTIVITY_ID', activityId);
            return apiClient
                .put(url, {
                    owner_id: ownerId,
                })
                .then(() => {
                    refetchTree();
                });
        },
        [updateProjectActivityOwnerUrl, refetchTree],
    );

    const allocateProjectResources = useCallback(
        ({ activityId, resources }) => {
            const url = allocateProjectResourcesUrl.replace('ACTIVITY_ID', activityId);
            return apiClient.post(url, { resources });
        },
        [allocateProjectResourcesUrl],
    );

    const moveItems = ({ originType, originId, destinationType, destinationId, originSource, destinationSource }) => {
        return apiClient
            .post(
                moveProjectGanttChartUrl,
                {
                    origin_type: originType,
                    origin_id: originId,
                    destination_type: destinationType,
                    destination_id: destinationId,
                    origin_source: originSource,
                    destination_source: destinationSource,
                },
                // A 422 here means the roll-up view (IB-842) surfaced a cost-centre activity
                // that cannot be dragged from this department project — an expected, already
                // user-messaged rejection (see the catch below), not an unexpected crash, so
                // it should not pop the global crash-report dialog. Other statuses (e.g. a
                // genuine 500) are unaffected and keep the default dialog behaviour.
                { expectedErrorStatuses: [422] },
            )
            .then(() => {
                toast.success('Items moved successfully');
                setTimeout(() => {
                    // Refetch the gantt chart data after moving items
                    refetchPlan();
                }, 500);
            })
            .catch((error) => {
                // The backend returns error text under `error`, not `message` — read both so
                // the tailored message (e.g. the 422 above) actually reaches the user.
                toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to move items');
            });
    };

    const value = {
        ganttChart: {
            data: projectGanttChartRequest.data,
            loading: projectGanttChartRequest.loading,
            // Loading a refetch that keeps the loaded window: the rows on screen are still valid.
            refreshing: projectGanttChartRequest.loading && keepingWindow,
            // Tree and schedule. Use `refetchAfterEdit` after an activity change, which can move links or bars.
            refetch: refetchPlan,
            refetchAfterEdit,
            progress: projectGanttChartProgress,
            filteredEmpty,
        },
        schedule,
        // The last schedule fetch failed; what `schedule` still holds may be out of date.
        scheduleFailed: !!scheduleRequest.error,
        hasKeyMilestones: !!schedule?.key_milestones?.length,
        changeTarget,
        sortByFloat,
        toggleSortByFloat,
        columnsByKey,
        loadedRange,
        line,
        anchorDate,
        loadColumnWindow,
        resources: {
            data: resourcesRequest.data?.data || [],
            loading: resourcesRequest.loading,
            refetch: resourcesRequest.refetch,
        },
        activityTypes,
        sourceCategories,
        statuses,
        filters,
        completenessStatuses,
        canManage,
        expandedRows,
        deletableRows,
        deletable,
        disabledDeletionRows,
        projectSourceType,
        projectSourceId,
        projectSourceCategory,
        isMaster,
        fetchProjectGanttChartLinksUrl,
        linksRevision,
        fetchProjectActivityResourcesUrl,
        fetchProjectActivityDependenciesUrl,
        searchProjectActivityDependencyCandidatesUrl,
        searchProjectCategoryDependencyCandidatesUrl,
        dependencyTypes,
        moveItems,
        toggleRow,
        setRowExpanded,
        toggleAllRows,
        toggleDeletableRow,
        toggleDeletable,
        changeFilters,
        removeProjectPlanItems,
        updateProjectCategoryOwner,
        updateProjectActivityOwner,
        allocateProjectResources,
    };

    return <ProjectPlanContext.Provider value={value}>{children}</ProjectPlanContext.Provider>;
};

export default ProjectPlanContext;
